import { useMutation, useQuery } from "react-query";
import {
  AssessmentContent,
  FormattedContent,
  Question,
} from "../../../../../@types";
import { ReactComponent as CollapseIcon } from "../../../../../assets/collapse.svg";
import { getCurrentAssessmentAnswers } from "../../../../../services/selfAssessment.service";
import { useContentActions } from "../../../../../hooks/useContentActions";
import { getUserContentAndProgressTree } from "../../../../../services/content.service";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import Button from "../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../components/button/types";

const AnswerWrapper = ({
  text,
  onClick,
  isActive,
}: {
  text: string;
  onClick: () => void;
  isActive: boolean;
}) => {
  return (
    <div
      onClick={onClick}
      className={`m-1 flex justify-center w-1/3 border p-3 rounded-md cursor-pointer transition-all ${
        isActive ? "bg-Grey-light border-black" : "bg-white"
      }`}
    >
      {text}
    </div>
  );
};

function AssessmentQuestion({
  question,
  selectedAnswer,
  onAnswerChange,
  selfAssessmentId,
  selfAssessmentState,
  refreshAnswers,
  setHasSelectedAnswer,
}: {
  question: Question;
  selectedAnswer: string | null;
  onAnswerChange: (
    questionId: string,
    selected: "Yes" | "Maybe" | "No" | ""
  ) => void;
  selfAssessmentId: string;
  selfAssessmentState: any;
  refreshAnswers: () => void;
  setHasSelectedAnswer: (value: boolean) => void;
}) {
  const { t } = useTranslation("content");

  const handleResponse = (selected: "Yes" | "Maybe" | "No") => {
    const newAnswer = selected === selectedAnswer ? "" : selected;
    onAnswerChange(question.id, newAnswer);
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 items-center mb-4 mt-4">
        <div className="mb-6 md:mb-3">{question.text}</div>
        <div className="flex justify-between">
          <AnswerWrapper
            isActive={selectedAnswer === "No"}
            onClick={() => handleResponse("No")}
            text={t("answers.No")}
          />
          <AnswerWrapper
            isActive={selectedAnswer === "Maybe"}
            onClick={() => handleResponse("Maybe")}
            text={t("answers.Maybe")}
          />
          <AnswerWrapper
            isActive={selectedAnswer === "Yes"}
            onClick={() => handleResponse("Yes")}
            text={t("answers.Yes")}
          />
        </div>
      </div>
      <hr className="border border-Grey-light-2" />
    </>
  );
}

export function Assessment({ content }: { content: AssessmentContent }) {
  const { t } = useTranslation("home");
  const { data: selfAssessmentState, refetch } = useQuery(
    ["self-assessment"],
    () => getCurrentAssessmentAnswers(content.id)
  );
  const { saveAnswers } = useContentActions({
    content: { id: content.id } as FormattedContent,
  });

  const { refetch: refreshUserProgressState } = useQuery(
    ["userContentProgress"],
    () => getUserContentAndProgressTree({ platformContentId: content.id })
  );

  const [hasSelectedAnswer, setHasSelectedAnswer] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<string, string | null>
  >({});

  useEffect(() => {
    const initialAnswers = selfAssessmentState?.answers?.reduce(
      (acc: Record<string, string | null>, answer: any) => {
        acc[answer.questionId] = answer.answer || null;
        return acc;
      },
      {}
    );
    setSelectedAnswers(initialAnswers || {});
  }, [selfAssessmentState]);

  const handleCancel = () => {
    setHasSelectedAnswer(false);
    const resetAnswers = selfAssessmentState?.answers?.reduce(
      (acc: Record<string, string | null>, answer: any) => {
        acc[answer.questionId] = answer.answer || null;
        return acc;
      },
      {}
    );
    setSelectedAnswers(resetAnswers || {});
  };

  const handleSave = () => {
    if (Object.keys(selectedAnswers).length > 0) {
      saveAnswers.mutate({ answers: selectedAnswers });
    }
    setHasSelectedAnswer(false);
  };

  const handleAnswerChange = (
    questionId: string,
    selected: "Yes" | "Maybe" | "No" | ""
  ) => {
    setSelectedAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: selected,
    }));
    setHasSelectedAnswer(true);
  };

  return (
    <>
      <div className="mt-10">
        <hr />
        <div className="flex justify-between mt-2">
          <h1 className="text-lg text-Grey-dark font-bold">
            {t("self-assessment")}
          </h1>
          <CollapseIcon className="cursor-pointer" />
        </div>
      </div>

      {content.questions?.map((q: Question) => (
        <AssessmentQuestion
          key={q.id}
          question={q}
          selectedAnswer={selectedAnswers[q.id]}
          onAnswerChange={handleAnswerChange}
          setHasSelectedAnswer={setHasSelectedAnswer}
          selfAssessmentState={selfAssessmentState}
          refreshAnswers={refetch}
          selfAssessmentId={content.id}
        />
      ))}

      {hasSelectedAnswer && (
        <div className="flex justify-end mt-4 lg:w-full space-x-4 lg:ml-2 lg:pr-2 lg:pl-8">
          <Button
            onClick={handleCancel}
            className="mb-2 lg:w-1/4 w-full"
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.SMALL}
          >
            {t("cancel")}
          </Button>

          <Button
            onClick={handleSave}
            className="mb-2 lg:w-1/4 w-full"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SMALL}
          >
            {t("save")}
          </Button>
        </div>
      )}

      <div
        className="py-6"
        dangerouslySetInnerHTML={{
          __html: content?.renderedContent || "",
        }}
      ></div>
    </>
  );
}
